import React from 'react';
import OptionalPhotoUpload from '../common/OptionalPhotoUpload';
import UnitRealMediaPicker from '../common/UnitRealMediaPicker';
import {
  buildDefaultUnitQcReport,
  UNIT_QC_LABELS,
  OLD_MOBILE_GRADE_OPTIONS
} from '../../constants/oldMobileQc';
import { isIphoneProduct, normalizeSerialInput } from '../../utils/iphoneHelpers';

const DEFAULT_UNIT_QC_REPORT = buildDefaultUnitQcReport();

/**
 * StockModal — nayi stock add karna (IMEI + Old Mobile QC support).
 * Sabhi state Inventory.js mein hai; ye component sirf UI hai.
 *
 * Props:
 *   show               — boolean
 *   products           — array (selectedProductId se product dhundhne ke liye)
 *   selectedProductId  — string | null
 *   stockAmount        — string
 *   imeiList           — array
 *   stockLoading       — boolean
 *   onStockAmountChange(val, newImeiList) — updates stockAmount + imeiList dono ek saath
 *   onImeiListChange(newList)             — sirf imeiList update
 *   onSubmit()         — submitStockIncrement
 *   onClose()          — setShowStockModal(false) + setImeiList([]) + setStockAmount('')
 */
export default function StockModal({
  show,
  products,
  selectedProductId,
  stockAmount,
  imeiList,
  stockLoading,
  onStockAmountChange,
  onImeiListChange,
  onSubmit,
  onClose
}) {
  if (!show) return null;

  const product = products.find((p) => p._id === selectedProductId);
  const isMobile = product && (product.category === 'Mobile' || product.category === 'Old Mobile');
  const isOldMobile = product && product.category === 'Old Mobile';
  const showSerial = product && isIphoneProduct(product.brand, product.name);

  const handleQuantityChange = (e) => {
    const qty = parseInt(e.target.value) || 0;
    const val = e.target.value;
    if (isMobile && qty > 0) {
      const newList = [];
      for (let i = 0; i < qty; i++) {
        if (isOldMobile) {
          newList.push({
            imei1: '', imei2: '', serialNo: '', sellerName: '', sellerPhone: '',
            unitConditionGrade: '', unitQcReport: { ...DEFAULT_UNIT_QC_REPORT },
            mediaMode: 'none',
            photoFiles: [],
            videoFile: null,
            sellerIntakePhoto: null
          });
        } else {
          newList.push({ imei1: '', imei2: '', serialNo: '', sellerName: '', index: i });
        }
      }
      onStockAmountChange(val, newList);
    } else {
      onStockAmountChange(val, []);
    }
  };

  const updateImei = (index, field, value) => {
    const updated = [...imeiList];
    updated[index] = { ...updated[index], [field]: value };
    onImeiListChange(updated);
  };

  const removeImei = (index) => {
    const updated = imeiList.filter((_, i) => i !== index);
    onStockAmountChange(updated.length.toString(), updated);
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className={`modal-dialog ${isOldMobile ? 'modal-xl' : 'modal-lg'}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Add Stock</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            <div className="form-group mb-3">
              <label htmlFor="stock-amount" className="form-label">Quantity to Add</label>
              <input
                type="number"
                className="form-control"
                id="stock-amount"
                placeholder="Enter quantity"
                value={stockAmount}
                onChange={handleQuantityChange}
                min="1"
              />
              <small className="text-muted">This will be added to the existing stock</small>
            </div>

            {isMobile && imeiList.length > 0 && (
              <div className="card">
                <div className="card-header bg-info text-white">
                  <h6 className="mb-0">
                    {isOldMobile
                      ? 'Old Mobile — IMEI + Seller + QC + Photos/Video (per unit)'
                      : 'IMEI + supplier / seller name (per unit)'}
                  </h6>
                </div>
                <div className="card-body">
                  <p className="text-muted mb-3">
                    {isOldMobile
                      ? 'Har unit ka alag record: sale / branch transfer ke baad bhi QC aur photos history me rahenge.'
                      : 'Har unit ke liye IMEI aur (optional) kis seller / supplier se liya — baad me IMEI history me dikhega.'}
                  </p>

                  {imeiList.map((imei, index) =>
                    isOldMobile ? (
                      <div key={index} className="border rounded p-3 mb-3 bg-light">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <strong className="text-primary">Unit #{index + 1}</strong>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeImei(index)}
                          >
                            <i className="bi bi-trash"></i> Remove
                          </button>
                        </div>
                        <div className="row">
                          <div className="col-md-6 mb-2">
                            <label className="form-label">IMEI 1 *</label>
                            <input
                              type="text" className="form-control" placeholder="15 digit IMEI"
                              value={imei.imei1} maxLength="15"
                              onChange={(e) => updateImei(index, 'imei1', e.target.value.replace(/\D/g, '').slice(0, 15))}
                            />
                          </div>
                          <div className="col-md-6 mb-2">
                            <label className="form-label">IMEI 2</label>
                            <input
                              type="text" className="form-control" placeholder="15 digit IMEI (if dual SIM)"
                              value={imei.imei2} maxLength="15"
                              onChange={(e) => updateImei(index, 'imei2', e.target.value.replace(/\D/g, '').slice(0, 15))}
                            />
                          </div>
                          {showSerial && (
                            <div className="col-md-6 mb-2">
                              <label className="form-label">
                                Serial no. <span className="text-muted">(iPhone, optional)</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="e.g. F2LM9K3PQ1GC"
                                value={imei.serialNo || ''}
                                maxLength="20"
                                onChange={(e) => updateImei(index, 'serialNo', normalizeSerialInput(e.target.value))}
                              />
                            </div>
                          )}
                          <div className="col-md-6 mb-2">
                            <label className="form-label">
                              Seller name <span className="text-muted">(optional)</span>
                            </label>
                            <input
                              type="text" className="form-control" value={imei.sellerName || ''}
                              onChange={(e) => updateImei(index, 'sellerName', e.target.value)}
                            />
                          </div>
                          <div className="col-md-6 mb-2">
                            <label className="form-label">Seller phone</label>
                            <input
                              type="text" className="form-control" value={imei.sellerPhone || ''}
                              onChange={(e) => updateImei(index, 'sellerPhone', e.target.value)}
                            />
                          </div>
                          <div className="col-md-6 mb-2">
                            <label className="form-label">Condition grade *</label>
                            <select
                              className="form-select"
                              value={imei.unitConditionGrade || ''}
                              onChange={(e) => updateImei(index, 'unitConditionGrade', e.target.value)}
                            >
                              <option value="">Select grade</option>
                              {OLD_MOBILE_GRADE_OPTIONS.map((g) => (
                                <option key={g.value} value={g.value}>{g.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <OptionalPhotoUpload
                          title="Seller / shop intake photo"
                          hint="Jab customer ne shop ko phone becha — seller profile → Sold to Shop me dikhegi."
                          file={imei.sellerIntakePhoto || null}
                          onChange={(f) => updateImei(index, 'sellerIntakePhoto', f)}
                          className="mb-2"
                        />
                        <UnitRealMediaPicker
                          inputName={`unit-real-media-mode-${index}`}
                          mediaMode={imei.mediaMode || 'none'}
                          onMediaModeChange={(m) => updateImei(index, 'mediaMode', m)}
                          photoFiles={imei.photoFiles || []}
                          onPhotoFilesChange={(files) => updateImei(index, 'photoFiles', files)}
                          videoFile={imei.videoFile || null}
                          onVideoFileChange={(f) => updateImei(index, 'videoFile', f)}
                        />
                        <details className="mt-1">
                          <summary className="small fw-semibold">QC checklist (this unit)</summary>
                          <div className="row mt-2 gx-2">
                            {Object.keys(UNIT_QC_LABELS).map((key) => {
                              const value = imei.unitQcReport?.[key];
                              const isWorking =
                                value === true || value === 'working' || value === undefined;
                              return (
                                <div key={key} className="col-lg-4 col-md-6 mb-2">
                                  <label className="form-check-label small d-flex align-items-center">
                                    <input
                                      type="checkbox" className="form-check-input me-2"
                                      checked={isWorking}
                                      onChange={(e) => {
                                        const qr = { ...(imei.unitQcReport || {}) };
                                        qr[key] = e.target.checked ? true : 'issue';
                                        updateImei(index, 'unitQcReport', qr);
                                      }}
                                    />
                                    {UNIT_QC_LABELS[key]}
                                  </label>
                                  {!isWorking ? (
                                    <input
                                      type="text"
                                      className="form-control form-control-sm mt-1"
                                      placeholder={`${UNIT_QC_LABELS[key]} issue / note`}
                                      value={typeof value === 'string' && value !== 'issue' ? value : ''}
                                      onChange={(e) => {
                                        const qr = { ...(imei.unitQcReport || {}) };
                                        qr[key] = e.target.value || 'issue';
                                        updateImei(index, 'unitQcReport', qr);
                                      }}
                                    />
                                  ) : null}
                                </div>
                              );
                            })}
                            <div className="col-12">
                              <label className="form-label small">Extra condition note</label>
                              <textarea
                                className="form-control form-control-sm"
                                rows="2"
                                placeholder="Example: back par scratch, battery normal, display minor line..."
                                value={imei.unitQcReport?.conditionNote || ''}
                                onChange={(e) => {
                                  const qr = { ...(imei.unitQcReport || {}) };
                                  qr.conditionNote = e.target.value;
                                  updateImei(index, 'unitQcReport', qr);
                                }}
                              />
                            </div>
                          </div>
                        </details>
                      </div>
                    ) : (
                      <div key={index} className="border rounded p-2 mb-3 bg-light">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <strong className="text-primary small">Unit #{index + 1}</strong>
                          <button
                            type="button" className="btn btn-sm btn-outline-danger"
                            onClick={() => removeImei(index)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                        <div className="row g-2">
                          <div className="col-md-6">
                            <label className="form-label">IMEI 1 *</label>
                            <input
                              type="text" className="form-control" placeholder="15 digit IMEI"
                              value={imei.imei1} maxLength="15" required
                              onChange={(e) => updateImei(index, 'imei1', e.target.value.replace(/\D/g, '').slice(0, 15))}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">IMEI 2</label>
                            <input
                              type="text" className="form-control" placeholder="15 digit IMEI (if dual SIM)"
                              value={imei.imei2} maxLength="15"
                              onChange={(e) => updateImei(index, 'imei2', e.target.value.replace(/\D/g, '').slice(0, 15))}
                            />
                          </div>
                          {showSerial && (
                            <div className="col-md-6">
                              <label className="form-label">
                                Serial no. <span className="text-muted">(optional)</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="iPhone serial"
                                value={imei.serialNo || ''}
                                maxLength="20"
                                onChange={(e) => updateImei(index, 'serialNo', normalizeSerialInput(e.target.value))}
                              />
                            </div>
                          )}
                          <div className="col-12">
                            <label className="form-label">Seller / supplier name</label>
                            <input
                              type="text" className="form-control"
                              placeholder="Kis seller / distributor se liya (optional)"
                              value={imei.sellerName || ''}
                              onChange={(e) => updateImei(index, 'sellerName', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    )
                  )}

                  <div className="alert alert-info">
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>Note:</strong>{' '}
                    {isOldMobile
                      ? 'Har IMEI par alag seller, QC aur photos save honge — transfer / sale ke baad bhi IMEI history me dikhenge.'
                      : 'Har IMEI = 1 unit. Seller naam optional — baad me IMEI Management me dikhega.'}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button" className="btn btn-primary"
              onClick={onSubmit} disabled={stockLoading}
            >
              {stockLoading ? 'Processing...' : 'Add Stock'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
